import { createFileRoute, Link } from "@tanstack/react-router";

import { ArrowLeft, Info } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  CarouselSwipeHint,
} from "@/components/ui/carousel";

export const Route = createFileRoute("/self-love")({
  head: () => ({
    meta: [
      { title: "Self-Love — Recovery Trauma™" },
      {
        name: "description",
        content:
          "Gentle practices to reconnect with self-worth, compassion, and acceptance.",
      },
      { property: "og:title", content: "Self-Love — Recovery Trauma™" },
      {
        property: "og:description",
        content:
          "Swipeable cards of warm, creative invitations to rebuild self-worth and speak to yourself with kindness.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://recoverytrauma.com/self-love" },
    ],
    links: [
      { rel: "canonical", href: "https://recoverytrauma.com/self-love" },
    ],
  }),
  component: SelfLovePage,
});

type Card = {
  id: string;
  emoji: string;
  title: string;
  description: string;
  ideas: string[];
  tint: string;
};

const CARDS: Card[] = [
  {
    id: "words",
    emoji: "✨",
    title: "Words of Kindness",
    description: "Speak to yourself the way you would to someone you love.",
    ideas: [
      "Write three things you appreciate about yourself",
      "Write down your strengths",
      "Speak to yourself as you would a close friend",
      "Write a love letter to your future self",
    ],
    tint: "#E8B4C8",
  },
  {
    id: "body",
    emoji: "🪞",
    title: "Body & Presence",
    description: "Small gestures that let you feel at home in yourself.",
    ideas: [
      "Look in the mirror and smile at yourself",
      "Hug yourself for 30 seconds",
      "Wear something that makes you feel good",
      "Dance to your favourite song",
    ],
    tint: "#D58FB4",
  },
  {
    id: "play",
    emoji: "📖",
    title: "Play & Nostalgia",
    description: "Return to the simple things that once made you sparkle.",
    ideas: [
      "Read your favourite childhood book",
      "Make a playlist that reminds you of happier times",
      "Buy yourself a small gift",
    ],
    tint: "#E8C07A",
  },
  {
    id: "space",
    emoji: "🗺️",
    title: "Space & Adventure",
    description: "Give yourself room to breathe, explore and simply be.",
    ideas: [
      "Take yourself on a solo adventure",
      "Take yourself on a nature walk",
      "Disconnect from social media for a few hours",
      "Give yourself permission to do nothing today",
    ],
    tint: "#B7C4E0",
  },
  {
    id: "growth",
    emoji: "🕊️",
    title: "Growth & Forgiveness",
    description: "Gentle steps back toward acceptance and possibility.",
    ideas: [
      "Forgive yourself for one mistake",
      "Create a vision board for your future",
      "Practise receiving compliments without dismissing them",
    ],
    tint: "#7BAE7F",
  },
];

const REFLECTION = [
  "Which invitation felt tender today?",
  "What softened in you as you tried it?",
  "What would you like to offer yourself tomorrow?",
];

function SelfLovePage() {
  return (
    <div className="mx-auto max-w-5xl px-4 py-10">
      <div className="mb-6">
        <Link
          to="/holistic-wellbeing"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Wellbeing Hub
        </Link>
      </div>

      <header className="mb-8 text-center">
        <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-gold/40 bg-cream/60 px-3 py-1 text-xs font-medium text-foreground">
          <span aria-hidden>💛</span> Self-Love
        </div>
        <h1 className="font-display text-3xl tracking-tight text-foreground sm:text-4xl md:text-5xl">
          Come home to yourself
        </h1>
        <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-muted-foreground md:text-base">
          Small, gentle practices to reconnect with worth, compassion and
          acceptance — swipe through and choose what feels tender today.
        </p>
      </header>

      <div className="mb-8 flex items-start gap-3 rounded-2xl border border-gold/30 bg-cream/40 p-4 text-sm text-foreground/85">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-[var(--gold-deep)]" />
        <p>
          Supportive, non-clinical practices — not therapy. If anything feels
          overwhelming, reach out to{" "}
          <Link to="/crisis" className="font-semibold underline">
            crisis resources
          </Link>
          .
        </p>
      </div>

      <Carousel
        opts={{ align: "start", loop: true }}
        className="w-full"
        aria-label="Self-love practices"
      >
        <CarouselSwipeHint label="Swipe to explore" storageKey="self-love" />
        <CarouselContent className="-ms-5">
          {CARDS.map((c) => (
            <CarouselItem
              key={c.id}
              className="basis-[88%] ps-5 sm:basis-1/2 lg:basis-1/2"
            >
              <article className="group relative flex h-full flex-col rounded-[2rem] border border-gold-light/60 bg-gradient-to-br from-[oklch(0.97_0.025_85)] via-card to-[oklch(0.93_0.05_82)] p-6 shadow-[0_24px_60px_-32px_color-mix(in_oklab,var(--gold-deep)_45%,transparent)] transition-all duration-300 hover:-translate-y-1 dark:border-gold-light/40 dark:from-[oklch(0.30_0.015_78)] dark:via-card dark:to-[oklch(0.34_0.04_80)] sm:p-8">
                <div className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--gold-deep)]">
                  <span aria-hidden>💛</span> Self-love
                </div>
                <div className="mt-3 flex items-start gap-4">
                  <div
                    aria-hidden
                    className="relative flex h-24 w-24 shrink-0 items-center justify-center rounded-3xl text-5xl ring-1 ring-gold-light/50 sm:h-28 sm:w-28 sm:text-6xl"
                    style={{
                      background: `radial-gradient(ellipse at center, color-mix(in oklab, ${c.tint} 30%, var(--cream)) 0%, color-mix(in oklab, ${c.tint} 12%, var(--cream)) 75%, transparent 100%)`,
                    }}
                  >
                    <span>{c.emoji}</span>
                  </div>
                  <div className="min-w-0 pt-1">
                    <h2 className="font-display text-xl leading-tight text-foreground sm:text-2xl">
                      {c.title}
                    </h2>
                  </div>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-foreground/80 sm:text-base">
                  {c.description}
                </p>
                <div className="mt-5">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--gold-deep)]">
                    Invitations
                  </div>
                  <ul className="mt-3 space-y-2 text-sm text-foreground/85">
                    {c.ideas.map((idea) => (
                      <li key={idea} className="flex items-start gap-2">
                        <span
                          aria-hidden
                          className="mt-1.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--gold-deep)]"
                        />
                        <span>{idea}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="mt-auto pt-6">
                  <div className="rounded-2xl border border-gold/30 bg-cream/50 p-4">
                    <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--gold-deep)]">
                      <span aria-hidden>💛</span> Gentle reflection
                    </div>
                    <ul className="mt-2 space-y-1.5 text-xs leading-relaxed text-foreground/85 sm:text-sm">
                      {REFLECTION.map((p) => (
                        <li key={p} className="flex items-start gap-2">
                          <span aria-hidden className="text-[var(--gold-deep)]">
                            •
                          </span>
                          <span>{p}</span>
                        </li>
                      ))}
                    </ul>
                    <Link
                      to="/journal"
                      className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[var(--gold-deep)] hover:underline"
                    >
                      Capture in Journal →
                    </Link>
                  </div>
                </div>
              </article>
            </CarouselItem>
          ))}
        </CarouselContent>
        <CarouselPrevious className="-start-3 hidden sm:flex" />
        <CarouselNext className="-end-3 hidden sm:flex" />
      </Carousel>
    </div>
  );
}
